import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { categoryById, type Category } from "../data/categories";
import { useData } from "../context/DataContext";
import { useReveal } from "../hooks/useReveal";
import { buildPool, suggest } from "../engine/suggest";
import { pickExcluding, shuffle } from "../engine/random";
import { local } from "../lib/storage";
import { TONE_BG } from "../lib/tones";
import { PageShell, Panel } from "../components/PageShell";
import { PopButton } from "../components/PopButton";
import { ResultReveal } from "../components/reveal/ResultReveal";
import NotFound from "./NotFound";
import { useSeo } from "../lib/seo";

function CustomOptions({ cat, custom, setCustom }: { cat: Category; custom: string[]; setCustom: (v: string[]) => void }) {
  const [text, setText] = useState("");
  const add = () => {
    const v = text.trim();
    if (!v) return;
    if (custom.some((c) => c.toLowerCase() === v.toLowerCase())) return toast.error("You've already added that one.");
    setCustom([...custom, v].slice(0, 30));
    setText("");
  };
  return (
    <div>
      <p className="mb-2 text-sm font-extrabold uppercase tracking-wide">Add your own</p>
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); add(); }}>
        <input className="field" maxLength={80} placeholder={cat.customPlaceholder || `e.g. ${cat.items[0].label}`} value={text} onChange={(e) => setText(e.target.value)} data-testid="custom-option-input" />
        <PopButton type="submit" tone="ink" size="sm" aria-label="Add option" data-testid="custom-option-add-button"><Plus className="h-5 w-5" strokeWidth={3} /></PopButton>
      </form>
      {!!custom.length && (
        <div className="mt-3 flex flex-wrap gap-2">
          {custom.map((c, i) => (
            <span key={c} className="chip inline-flex max-w-full items-center gap-1 bg-pop-yellow" data-testid={`custom-option-${i}`}>
              <span className="truncate">{c}</span>
              <button aria-label={`Remove ${c}`} onClick={() => setCustom(custom.filter((x) => x !== c))} data-testid={`custom-option-remove-${i}`}><X className="h-3.5 w-3.5" strokeWidth={3} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CategoryPage() {
  const { categoryId } = useParams();
  const cat = categoryById(categoryId);
  const { prefs, recordDislike } = useData();
  const { spec, show, close } = useReveal();
  const [filters, setFilters] = useState<string[]>([]);
  const [custom, setCustomState] = useState<string[]>(() => local.customOptions(categoryId || ""));
  const [customOnly, setCustomOnly] = useState(false);

  const setCustom = (v: string[]) => {
    setCustomState(v);
    local.setCustomOptions(cat.id, v);
    if (!v.length) setCustomOnly(false);
  };
  const opts = { filters, custom, customOnly, dislikes: prefs.dislikes };
  useSeo({
    title: cat ? `${cat.question} Random ${cat.name} Picker | DITHER` : "DITHER",
    description: cat ? `${cat.question} Let DITHER spin the wheel on ${cat.items.slice(0, 3).map((i) => i.label.toLowerCase()).join(", ")} and more — or add your own options.` : "",
    noindex: !cat,
  });
  const peopleMode = cat?.id === "chores" && custom.length > 0;
  const chores = useMemo(() => (cat ? buildPool(cat, { filters }) : []), [cat, filters]);
  const poolSize = useMemo(() => (cat ? buildPool(cat, { filters, custom, customOnly }).length : 0), [cat, filters, custom, customOnly]);
  if (!cat) return <NotFound />;

  const runPeople = () => {
    const name = pickExcluding(custom);
    const chore = pickExcluding(chores);
    show({ kind: "category", title: cat.question, result: name, detail: `→ ${chore}`, anim: "wheel", reel: shuffle([...custom, ...custom].slice(0, Math.max(4, custom.length))), category: cat.id });
  };

  const run = (exclude?: string) => {
    if (peopleMode) return runPeople();
    try {
      const s = suggest(cat, { ...opts, exclude });
      show({ kind: "category", title: cat.question, result: s.result, anim: "wheel", reel: s.wheel, more: s.more, category: cat.id });
    } catch (e: any) {
      toast.error(e.message);
    }
  };
  const toggle = (id: string) => setFilters((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));

  return (
    <PageShell title={cat.name} subtitle={cat.question} testId={`category-page-${cat.id}`}>
      <Panel className="space-y-6">
        <div>
          <p className="mb-2 text-sm font-extrabold uppercase tracking-wide">Narrow it down <span className="font-semibold normal-case text-zinc-500">(optional)</span></p>
          <div className="flex flex-wrap gap-2">
            {cat.filters.map((f) => (
              <button key={f.id} className="chip" data-active={filters.includes(f.id)} onClick={() => toggle(f.id)} data-testid={`filter-chip-${f.id}`}>{f.label}</button>
            ))}
          </div>
        </div>
        {cat.allowCustom && <CustomOptions cat={cat} custom={custom} setCustom={setCustom} />}
        {!!custom.length && !peopleMode && (
          <label className="flex cursor-pointer items-center gap-2 text-sm font-bold">
            <input type="checkbox" className="h-5 w-5 accent-ink" checked={customOnly} onChange={(e) => setCustomOnly(e.target.checked)} data-testid="custom-only-checkbox" />
            Only pick from my options
          </label>
        )}
        <div>
          <PopButton tone={cat.tone} size="xl" className="w-full" disabled={peopleMode ? !chores.length : !poolSize} onClick={() => run()} data-testid="category-dither-button">Dither it</PopButton>
          <p className="mt-2 text-center text-xs font-bold text-zinc-500" data-testid="category-pool-count">
            {peopleMode
              ? (chores.length ? `Picking from ${custom.length} ${custom.length === 1 ? "person" : "people"} and ${chores.length} chores` : "No chores match — loosen the filters.")
              : poolSize ? `Picking from ${poolSize} options` : "No options match — loosen the filters or add your own."}
          </p>
        </div>
      </Panel>
      {cat.id === "presents" && (
        <Link to="/presents" data-testid="category-present-finder-link" className={`card-pop mt-6 flex items-center justify-between rounded-[22px] p-5 ${TONE_BG.pink}`}>
          <span><span className="block font-display text-xl font-bold uppercase">Need a smarter pick?</span><span className="text-sm font-semibold">Try the Present Finder — who, budget, vibe.</span></span>
          <ArrowRight className="h-6 w-6" strokeWidth={3} />
        </Link>
      )}
      <ResultReveal spec={spec} onClose={close}
        onAgain={() => { if (spec) { recordDislike(spec.result); run(spec.result); } }} />
    </PageShell>
  );
}
